import { Field } from "@/components/admin/field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  INTERVIEW_FORMAT_LABELS,
  type InterviewCreate,
  type InterviewFormat,
  RECOMMENDATION_LABELS,
  type Recommendation,
} from "@ow/shared";
import { useState } from "react";

const FORMATS: InterviewFormat[] = ["video", "in_person", "panel"];
const RECS: Recommendation[] = ["proceed", "hold", "reject"];

/** Existing-interview shape used to prefill the form when editing. */
export interface InterviewInitial {
  roundName?: string;
  dateConducted?: string | null;
  format?: InterviewFormat | null;
  interviewers?: string[];
  feedbackNotes?: string | null;
  recommendation?: Recommendation | null;
  isPrivate?: boolean;
}

const today = () => new Date().toISOString().slice(0, 10);

export function InterviewForm({
  onSubmit,
  submitting,
  initial,
  defaultRound,
  onCancel,
  submitLabel,
}: {
  onSubmit: (b: InterviewCreate) => void;
  submitting?: boolean;
  /** When present, the form is in edit mode (fields prefilled). */
  initial?: InterviewInitial;
  /** Suggested round name (e.g. derived from the candidate's current stage). */
  defaultRound?: string;
  onCancel?: () => void;
  submitLabel?: string;
}) {
  const isEdit = initial != null;
  const [roundName, setRoundName] = useState(initial?.roundName ?? defaultRound ?? "");
  const [date, setDate] = useState(initial?.dateConducted ?? (isEdit ? "" : today()));
  const [format, setFormat] = useState<InterviewFormat | "">(initial?.format ?? "");
  const [interviewers, setInterviewers] = useState((initial?.interviewers ?? []).join(", "));
  const [notes, setNotes] = useState(initial?.feedbackNotes ?? "");
  const [rec, setRec] = useState<Recommendation | "">(initial?.recommendation ?? "");
  const [isPrivate, setIsPrivate] = useState(initial?.isPrivate ?? false);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!roundName.trim()) return;
    onSubmit({
      roundName: roundName.trim(),
      dateConducted: date || null,
      format: format || null,
      interviewers: interviewers
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      feedbackNotes: notes || null,
      recommendation: rec || null,
      isPrivate,
    });
    if (!isEdit) {
      setRoundName("");
      setDate(today());
      setFormat("");
      setInterviewers("");
      setNotes("");
      setRec("");
      setIsPrivate(false);
    }
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-2 gap-3 rounded-lg border bg-card p-3">
      <Field label="Round">
        <Input value={roundName} onChange={(e) => setRoundName(e.target.value)} placeholder="Round 1 interview" />
      </Field>
      <Field label="Date">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>
      <Field label="Format">
        <select
          value={format}
          onChange={(e) => setFormat(e.target.value as InterviewFormat)}
          className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
        >
          <option value="">—</option>
          {FORMATS.map((f) => (
            <option key={f} value={f}>
              {INTERVIEW_FORMAT_LABELS[f]}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Recommendation">
        <select
          value={rec}
          onChange={(e) => setRec(e.target.value as Recommendation)}
          className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
        >
          <option value="">—</option>
          {RECS.map((r) => (
            <option key={r} value={r}>
              {RECOMMENDATION_LABELS[r]}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Interviewers (comma-separated)" className="col-span-2">
        <Input value={interviewers} onChange={(e) => setInterviewers(e.target.value)} placeholder="Chair, CEO" />
      </Field>
      <Field label="Feedback notes" className="col-span-2">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
      <label className="col-span-2 flex items-center gap-2 text-sm">
        <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} />
        Private — never shown to client viewers
      </label>
      <div className="col-span-2 flex items-center gap-2">
        <Button type="submit" size="sm" disabled={submitting || !roundName.trim()}>
          {submitLabel ?? (isEdit ? "Save interview" : "Add interview")}
        </Button>
        {onCancel ? (
          <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
    </form>
  );
}
